import React, { useEffect, useState } from 'react';
import { ChevronDown, FileBadge2, IdCard, Receipt } from 'lucide-react';

// Portfolio → Corporate (variant="tab", the seat Maintenance had).
//
// What the investor holds and has done with us, from the CRM SaaS, across both
// companies: trade licences and visas (mostly Corporate Services Provider) and
// every transaction — Golden Visa, POA, DLD fees, company formation, VAT/CT …
// (Corporate Services Provider and Next Level Consulting). Each line names the
// company it came from.
//
// Source: GET /api/client-services (Backend/clientServicesRoutes.js), which
// looks the client up by the signed-in account's email. The tab says so when
// the link is off (404) or the client has nothing on file; the "section"
// variant instead renders nothing in those cases.

interface Licence { id: string; source: string; company: string; jurisdiction: string | null; number: string | null; expires: string | null; status: string | null; active: boolean }
interface Visa { id: string; source: string; holder: string; type: string | null; company: string | null; numberLast4: string | null; expires: string | null; status: string }
// One INVOICED deal (app-client-summary returns nothing without an invoice no.).
// Only these six reach the app: invoice number, amount, payment status, stage,
// unit, service type. Nothing else (no titles, dates or 3rd-party costs).
interface Txn {
  id: string; invoiceNumber: string;
  amount: number; currency: string;                      // what the client pays, incl. VAT
  paymentStatus: string | null; stage: string | null;
  property: string | null;                               // the unit, when the service is for one
  service: string | null;
}
interface Summary { found: boolean; licences: Licence[]; visas: Visa[]; transactions: Txn[] }

const GOLD = '#D6B160';
const INK = '#000000';
const MUTED = 'rgba(0,0,0,0.55)';

const daysUntil = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso); if (isNaN(d.getTime())) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - t.getTime()) / 86400000);
};
const fmtDate = (iso: string | null, de: boolean) =>
  iso ? new Date(iso).toLocaleDateString(de ? 'de-DE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtMoney = (n: number, cur: string, de: boolean) => `${cur} ${n.toLocaleString(de ? 'de-DE' : 'en-US', { maximumFractionDigits: 0 })}`;
// "Next Level Consulting FZCO" → "Next Level Consulting"; keeps the label short on a phone.
// German names for the CRM's service types and pipeline stages. Whole names
// first; otherwise word by word, so "Property Snagging + Admin Fee" still
// reads German. Names not listed (and admin-fee titles) stay as written.
const SERVICE_DE: Record<string, string> = {
  'power of attorney': 'Vollmacht', 'poa': 'Vollmacht', 'sales progression': 'Verkaufsabwicklung',
  'property snagging': 'Mängelabnahme', 'snagging': 'Mängelabnahme', 'hand over': 'Übergabe', 'handover': 'Übergabe',
  'property management': 'Hausverwaltung', 'management fee': 'Verwaltungsgebühr', 'admin fee': 'Bearbeitungsgebühr',
  'valuation': 'Bewertung', 'trustee': 'Treuhänder', 'trustee fee': 'Treuhändergebühr', 'transfer fee': 'Umschreibungsgebühr',
  'cleaning': 'Reinigung', 'cleaning services': 'Reinigung', 'escrow': 'Treuhandkonto', 'gifting': 'Schenkung',
  'golden visa': 'Golden Visa', 'visa': 'Visum', 'company formation': 'Firmengründung', 'company setup': 'Firmengründung',
  'license renewal': 'Lizenzverlängerung', 'licence renewal': 'Lizenzverlängerung', 'vat registration': 'USt-Registrierung',
  'corporate tax': 'Körperschaftsteuer', 'corporate tax registration': 'Körperschaftsteuer-Registrierung',
  'bank account opening': 'Kontoeröffnung', 'emirates id': 'Emirates ID', 'rental': 'Vermietung', 'tenancy': 'Mietvertrag',
};
const WORD_DE: Record<string, string> = {
  property: 'Immobilie', snagging: 'Mängelabnahme', admin: 'Bearbeitungs', fee: 'gebühr', handling: 'Abwicklungs',
  registration: 'Registrierung', updates: 'Updates', investor: 'Investor', renewal: 'Verlängerung', management: 'Verwaltung',
  valuation: 'Bewertung', cleaning: 'Reinigung', transfer: 'Umschreibung', sale: 'Verkauf', sales: 'Verkauf', and: 'und',
};
const serviceDe = (name: string | null, de: boolean): string | null => {
  if (!name || !de) return name;
  const key = name.trim().toLowerCase();
  if (SERVICE_DE[key]) return SERVICE_DE[key];
  return name.replace(/admin fee/gi, 'Bearbeitungsgebühr').replace(/[A-Za-z]+/g, (w) => {
    const d = WORD_DE[w.toLowerCase()];
    return d && d !== 'gebühr' && d !== 'Bearbeitungs' ? d : w;
  });
};
const STAGE_DE: Record<string, string> = {
  completed: 'Abgeschlossen', complete: 'Abgeschlossen', done: 'Erledigt', closed: 'Abgeschlossen', won: 'Abgeschlossen',
  'in progress': 'In Bearbeitung', new: 'Neu', lead: 'Neu', pending: 'Ausstehend', invoiced: 'Fakturiert',
  'proposal sent': 'Angebot gesendet', proposal: 'Angebot', negotiation: 'Verhandlung', 'on hold': 'Pausiert',
  'awaiting documents': 'Unterlagen ausstehend', submitted: 'Eingereicht', approved: 'Genehmigt', lost: 'Verloren',
};
const stageDe = (stage: string, de: boolean) => (de ? STAGE_DE[stage.trim().toLowerCase()] || stage : stage);
// "Golf Vista Heights 1412 #1412" -> "Golf Vista Heights 1412"
const unitClean = (p: string) => {
  const m = p.match(/^(.*?)\s*#\s*(\S+)$/);
  return m && m[1].split(/[^A-Za-z0-9-]+/).includes(m[2]) ? m[1] : p;
}

const shortSource = (s: string) => s.replace(/\s+(FZCO|LLC|FZE|FZ-LLC)\.?$/i, '').replace(/^Investmenter\s+/i, '').trim();

function ExpiryChip({ iso, de }: { iso: string | null; de: boolean }) {
  const d = daysUntil(iso);
  if (d === null) return null;
  const [bg, fg, text] =
    d < 0 ? ['#fdecec', '#b42318', de ? 'Abgelaufen' : 'Expired']
    : d <= 90 ? ['#fff6e5', '#9a6700', de ? `${d} Tage` : `${d} days`]
    : ['#eef7f0', '#1f7a3d', de ? 'Gültig' : 'Valid'];
  return <span style={{ background: bg, color: fg, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{text}</span>;
}

const Chip: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({ bg, fg, children }) => (
  <span style={{ background: bg, color: fg, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{children}</span>
);
const INVOICE: Record<string, [string, string, string]> = {
  paid: ['Paid', 'Bezahlt', '#1f7a3d'], partial: ['Part-paid', 'Teilbezahlt', '#9a6700'],
  unpaid: ['Unpaid', 'Offen', '#b42318'], credited: ['Credited', 'Gutgeschrieben', MUTED],
};

const Row: React.FC<{ icon: React.ReactNode; title: React.ReactNode; sub: React.ReactNode; right?: React.ReactNode }> = ({ icon, title, sub, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(214,177,96,0.12)', color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
      <div style={{ fontSize: 12, color: MUTED, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
    </div>
    {right && <div style={{ textAlign: 'right', flexShrink: 0 }}>{right}</div>}
  </div>
);

const Group: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginTop: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: GOLD }}>{label}</div>
    {children}
  </div>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: '28px 8px', textAlign: 'center', fontSize: 13.5, color: MUTED, lineHeight: 1.5 }}>{children}</div>
);

// variant "tab"  — the Portfolio → Corporate tab: always renders, with its own
//                  loading / empty states, licences and visas on top and the
//                  transactions below.
// variant "section" — a block for a longer page: renders nothing until there is data.
const ClientServicesSection: React.FC<{ apiUrl?: string; language: string; variant?: 'tab' | 'section' }> = ({ apiUrl, language, variant = 'section' }) => {
  const de = language === 'de';
  const tab = variant === 'tab';
  const [data, setData] = useState<Summary | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'off'>('loading');
  const [allTxns, setAllTxns] = useState(false);

  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!apiUrl || !token) { setState('off'); return; }
    let alive = true;
    // The last answer is kept on the device (per signed-in session), so the tab
    // shows at once and refreshes quietly behind it — the backend can be slow
    // for reasons that have nothing to do with this tab (a valuation run holds
    // every request up). Keyed by a hash of the session token: another account
    // on the same device never sees it.
    let h = 5381; for (let i = 0; i < token.length; i++) h = ((h << 5) + h + token.charCodeAt(i)) | 0;
    const cacheKey = `clientServices_${(h >>> 0).toString(36)}`;
    let cached: Summary | null = null;
    try { const raw = localStorage.getItem(cacheKey); if (raw) cached = JSON.parse(raw); } catch { /* storage blocked */ }
    if (cached) { setData(cached); setState('ready'); } else setState('loading');
    // "Try again" also asks the backend for a fresh copy, not its 5-minute one.
    const fresh = attempt > 0 ? '?refresh=1' : '';
    // A backend that has just restarted answers slowly for a minute or two and
    // can time out its call to the CRM — so a failure is retried twice (4 s,
    // then 10 s) before the tab says so, and the message carries a Try again.
    const load = async () => {
      for (const wait of [0, 4000, 10000]) {
        if (wait) await new Promise(res => setTimeout(res, wait));
        if (!alive) return;
        try {
          const r = await fetch(`${apiUrl}/client-services${fresh}`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.status === 404) break;               // link switched off — retrying won't help
          if (r.ok) {
            const j = await r.json();
            if (alive) { setData(j); setState('ready'); }
            try { localStorage.setItem(cacheKey, JSON.stringify(j)); } catch { /* storage full/blocked */ }
            return;
          }
        } catch { /* network / timeout — retry */ }
      }
      // Keep showing the device copy rather than an error when we have one.
      if (alive && !cached) setState('off');
    };
    load();
    return () => { alive = false; };
  }, [apiUrl, attempt]);

  const licences = (data?.licences || []).filter(l => l.active);
  const visas = data?.visas || [];
  const txns = data?.transactions || [];
  const empty = !licences.length && !visas.length && !txns.length;
  const limit = tab ? 5 : 4;   // the rest behind "Show more"
  const shownTxns = allTxns ? txns : txns.slice(0, limit);

  if (!tab && (state !== 'ready' || empty)) return null;
  const wrap = (body: React.ReactNode) => (
    <div className="section client-services-section" style={{ fontFamily: 'Montserrat, sans-serif', ...(tab ? { marginTop: 16 } : {}) }}>
      <div className="section-header">
        <h2>{tab ? (de ? 'Lizenzen, Visa & Transaktionen' : 'Licences, visas & transactions') : (de ? 'Ihre Leistungen bei uns' : 'Your services with us')}</h2>
      </div>
      {tab && <div style={{ fontSize: 12.5, color: MUTED, marginTop: -4 }}>
        {de ? 'Von Corporate Services Provider und Next Level Consulting' : 'From Corporate Services Provider and Next Level Consulting'}
      </div>}
      {body}
    </div>
  );
  if (state === 'loading') return wrap(<Note>{de ? 'Wird geladen…' : 'Loading…'}</Note>);
  if (state === 'off') return wrap(<Note>
    {de ? 'Ihre Unternehmensdaten sind gerade nicht verfügbar.' : 'Your company records are not available right now.'}
    <div style={{ marginTop: 12 }}>
      <button onClick={() => setAttempt(a => a + 1)}
        style={{ background: INK, color: '#fff', border: 'none', borderRadius: 999, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
        {de ? 'Erneut versuchen' : 'Try again'}
      </button>
    </div>
  </Note>);
  if (empty) return wrap(<Note>{de
    ? 'Für Ihr Konto sind noch keine Lizenzen, Visa oder Transaktionen hinterlegt. Gründen Sie ein Unternehmen oder beantragen Sie ein Visum über Mehr → Unternehmensservices UAE.'
    : 'No licences, visas or transactions on file for your account yet. Set up a company or apply for a visa under More → Corporate Services UAE.'}</Note>);

  return wrap(
    <>

      {licences.length > 0 && (
        <Group label={de ? 'Gewerbelizenzen' : 'Trade licences'}>
          {licences.map(l => (
            <Row key={l.id} icon={<FileBadge2 size={18} />}
              title={l.company}
              sub={[l.jurisdiction, l.number ? `No. ${l.number}` : null, `${de ? 'bis' : 'expires'} ${fmtDate(l.expires, de)}`].filter(Boolean).join(' · ')}
              right={<ExpiryChip iso={l.expires} de={de} />} />
          ))}
        </Group>
      )}

      {visas.length > 0 && (
        <Group label={de ? 'Visa' : 'Visas'}>
          {visas.map(v => (
            <Row key={v.id} icon={<IdCard size={18} />}
              title={v.holder}
              sub={[v.type ? `${v.type.charAt(0).toUpperCase()}${v.type.slice(1)} ${de ? 'Visum' : 'visa'}` : null, v.company, `${de ? 'bis' : 'expires'} ${fmtDate(v.expires, de)}`].filter(Boolean).join(' · ')}
              right={v.status === 'in_renewal'
                ? <span style={{ background: '#eef2fb', color: '#2f4f9a', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{de ? 'In Verlängerung' : 'Renewing'}</span>
                : <ExpiryChip iso={v.expires} de={de} />} />
          ))}
        </Group>
      )}

      {txns.length > 0 && (
        <Group label={de ? 'Transaktionen' : 'Transactions'}>
          {shownTxns.map(t => {
            const pay = t.paymentStatus ? INVOICE[t.paymentStatus] : null;
            return (
              <div key={t.id} style={{ display: 'flex', gap: 12, padding: '12px 0', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(214,177,96,0.12)', color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Receipt size={18} /></div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  {/* Service type, the unit (if any), the invoice number, the stage. */}
                  <div style={{ fontSize: 14, fontWeight: 700, color: INK }}>{serviceDe(t.service, de) || (de ? 'Leistung' : 'Service')}</div>
                  {t.property && <div style={{ fontSize: 12.5, color: INK, marginTop: 2 }}>{unitClean(t.property)}</div>}
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{de ? 'Rechnung' : 'Invoice'} {t.invoiceNumber}</div>
                  {t.stage && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                      {/^(completed?|done|closed|won)$/i.test(t.stage)
                        ? <Chip bg="#eef7f0" fg="#1f7a3d">{stageDe(t.stage, de)}</Chip>
                        : <Chip bg="#fff6e5" fg="#9a6700">{stageDe(t.stage, de)}</Chip>}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{fmtMoney(t.amount, t.currency, de)}</div>
                  <div style={{ fontSize: 10.5, color: MUTED }}>{de ? 'inkl. MwSt.' : 'incl. VAT'}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: pay ? pay[2] : MUTED, marginTop: 4 }}>{pay ? (de ? pay[1] : pay[0]) : (de ? 'Status offen' : 'Status pending')}</div>
                </div>
              </div>
            );
          })}
          {txns.length > limit && (
            <button className="expand-btn" onClick={() => setAllTxns(v => !v)}>
              {allTxns ? (de ? 'Weniger anzeigen' : 'Show less') : `${de ? 'Mehr anzeigen' : 'Show more'} (${txns.length - limit})`}
              <ChevronDown className={`chevron ${allTxns ? 'expanded' : ''}`} size={16} />
            </button>
          )}
        </Group>
      )}
    </>
  );
};

export default ClientServicesSection;
